import React, { useState } from "react";
import {
  Alert,
  Badge,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { RefreshCw, CheckCircle, XCircle } from "react-feather";
import { Button } from "react-bootstrap";
import { useSearchParams } from "react-router";
import { useGet } from "../../api";
import {
  IAutoCouponEvaluationListResponse,
  IAutoCouponEvaluation,
  TSessionRunPartStatus,
} from "../../interfaces";

// ─── Manual QA verdict badge (auto / SKU template pass·fail·not-tested) ──────

const PART_META: Record<
  TSessionRunPartStatus,
  { label: string; bg: string; text?: "dark" }
> = {
  pass: { label: "pass", bg: "success" },
  fail: { label: "fail", bg: "danger" },
  not_tested: { label: "not tested", bg: "light", text: "dark" },
};

const PartBadge: React.FC<{ status: TSessionRunPartStatus | undefined }> = ({
  status,
}) => {
  const meta = PART_META[status ?? "not_tested"] ?? PART_META.not_tested;
  return (
    <Badge bg={meta.bg} text={meta.text}>
      {meta.label}
    </Badge>
  );
};

// ─── View badge (desktop / mobile / both) ────────────────────────────────────

const ViewBadge: React.FC<{ view: string | null | undefined }> = ({ view }) => {
  if (!view) return <span className="text-muted small">—</span>;
  const bg =
    view === "mobile" ? "warning" : view === "both" ? "secondary" : "info";
  return (
    <Badge bg={bg} text={bg === "warning" ? "dark" : undefined}>
      {view}
    </Badge>
  );
};

// ─── Evaluation detail modal ───────────────────────────────────────────────

const EvaluationDetailModal: React.FC<{
  evaluation: IAutoCouponEvaluation | null;
  onClose: () => void;
}> = ({ evaluation, onClose }) => {
  const [showTemplate, setShowTemplate] = useState(false);
  const [showSku, setShowSku] = useState(false);

  const bestSaving =
    evaluation?.codes.reduce(
      (max, c) => (c.success && c.savings > max ? c.savings : max),
      0
    ) ?? 0;

  return (
    <Modal show={!!evaluation} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          Evaluation — {evaluation?.domain} / {evaluation?.country}
          <span className="font-monospace small text-muted ms-2">
            {evaluation?.correlationId?.slice(0, 8)}…
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {evaluation && (
          <>
            {/* Summary */}
            <Row className="mb-3 g-2">
              <Col xs={6} md={3}>
                <small className="text-muted d-block">Original total</small>
                <strong>
                  {evaluation.originalTotal != null
                    ? `${evaluation.currency ?? ""}${evaluation.originalTotal.toFixed(2)}`
                    : "—"}
                </strong>
              </Col>
              <Col xs={6} md={3}>
                <small className="text-muted d-block">Best saving</small>
                <strong className={bestSaving > 0 ? "text-success" : ""}>
                  {bestSaving > 0
                    ? `${evaluation.currency ?? ""}${bestSaving.toFixed(2)}`
                    : "—"}
                </strong>
              </Col>
              <Col xs={6} md={3}>
                <small className="text-muted d-block">Mode</small>
                <Badge
                  bg={evaluation.templateMode === "new" ? "warning" : "info"}
                  text="dark"
                >
                  {evaluation.templateMode}
                </Badge>
              </Col>
              <Col xs={6} md={3}>
                <small className="text-muted d-block">AI called</small>
                {evaluation.aiCalled ? (
                  <Badge bg="primary">Yes</Badge>
                ) : (
                  <span className="text-muted small">No</span>
                )}
              </Col>
              <Col xs={6} md={3}>
                <small className="text-muted d-block">Run by</small>
                {evaluation.origin === "session" ? (
                  <Badge bg="dark">Agentic (session)</Badge>
                ) : (
                  <Badge bg="light" text="dark">
                    User (extension)
                  </Badge>
                )}
              </Col>
              <Col xs={6} md={3}>
                <small className="text-muted d-block">User</small>
                <strong className="small">{evaluation.userEmail ?? "—"}</strong>
              </Col>
              <Col xs={6} md={3}>
                <small className="text-muted d-block">View</small>
                <ViewBadge view={evaluation.templateView} />
              </Col>
              <Col xs={6} md={3}>
                <small className="text-muted d-block">Auto version</small>
                <strong className="small">
                  {evaluation.version != null ? `v${evaluation.version}` : "—"}
                </strong>
              </Col>
              <Col xs={6} md={3}>
                <small className="text-muted d-block">Extension version</small>
                <strong className="small font-monospace">
                  {evaluation.extensionVersion
                    ? `v${evaluation.extensionVersion}`
                    : "—"}
                </strong>
              </Col>
            </Row>

            {/* Manual QA verdict — auto & SKU templates reported separately */}
            <Row className="mb-3 g-2">
              <Col xs={6} md={3}>
                <small className="text-muted d-block">Auto template</small>
                <PartBadge status={evaluation.autoTemplateStatus} />
                {evaluation.autoFailureReason && (
                  <div className="small text-danger mt-1">
                    {evaluation.autoFailureReason}
                  </div>
                )}
                {/* Backend-authoritative reason the last save attempt was
                    rejected (e.g. AUTO_INCOMPLETE) — set directly by the save
                    route, independent of whether the reviewer's verdict PATCH
                    landed, so it survives a race/failure in that request. */}
                {evaluation.templateSaveError && (
                  <div className="small text-danger mt-1">
                    ⚠ Save rejected: {evaluation.templateSaveError}
                  </div>
                )}
              </Col>
              <Col xs={6} md={3}>
                <small className="text-muted d-block">SKU template</small>
                <PartBadge status={evaluation.skuTemplateStatus} />
                {evaluation.skuFailureReason && (
                  <div className="small text-danger mt-1">
                    {evaluation.skuFailureReason}
                  </div>
                )}
                {evaluation.skuTemplateSaveError && (
                  <div className="small text-danger mt-1">
                    ⚠ Save rejected: {evaluation.skuTemplateSaveError}
                  </div>
                )}
              </Col>
            </Row>

            {/* Checkout-page URL regexes the run matched against (auto vs SKU) */}
            <Row className="mb-3 g-2">
              <Col xs={12} md={6}>
                <small className="text-muted d-block">
                  Auto checkout URL regex
                </small>
                <code className="small text-break">
                  {evaluation.checkoutPageUrlRegex ?? "—"}
                </code>
              </Col>
              <Col xs={12} md={6}>
                <small className="text-muted d-block">
                  SKU cart URL regex
                </small>
                <code className="small text-break">
                  {evaluation.skuCheckoutPageUrlRegex ?? "—"}
                </code>
              </Col>
            </Row>

            {/* Coupon codes table */}
            <h6 className="mb-2">Coupon codes ({evaluation.codes.length})</h6>
            {evaluation.codes.length === 0 ? (
              <p className="text-muted small">No codes evaluated.</p>
            ) : (
              <Table size="sm" bordered responsive className="mb-3">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Code</th>
                    <th>Summary</th>
                    <th>Result</th>
                    <th>Savings</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {evaluation.codes.map((code, i) => (
                    <tr key={code.id}>
                      <td className="text-muted small">{i + 1}</td>
                      <td className="font-monospace small">{code.code}</td>
                      <td className="small text-muted">
                        {code.summary || "—"}
                      </td>
                      <td>
                        {code.notApplicableToBasket ? (
                          <Badge bg="info">Not Applicable</Badge>
                        ) : code.success ? (
                          <Badge bg="success">Applied</Badge>
                        ) : (
                          <Badge bg="danger">Failed</Badge>
                        )}
                      </td>
                      <td>
                        {code.success && code.savings > 0
                          ? `${evaluation.currency ?? ""}${code.savings.toFixed(2)}`
                          : "—"}
                      </td>
                      <td className="small text-muted">
                        {code.errorMessage ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            {/* Auto template selectors (collapsible) */}
            {evaluation.template && (
              <>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <h6 className="mb-0">Auto template selectors</h6>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setShowTemplate((v) => !v)}
                  >
                    {showTemplate ? "Hide" : "Show"}
                  </Button>
                </div>
                {showTemplate && (
                  <pre
                    className="bg-light p-2 rounded small"
                    style={{ maxHeight: 250, overflowY: "auto", fontSize: 11 }}
                  >
                    {JSON.stringify(evaluation.template, null, 2)}
                  </pre>
                )}
              </>
            )}

            {/* SKU template selectors (collapsible) — the SKU half that ran,
                now stored on the evaluation itself rather than being dropped. */}
            {evaluation.skuTemplate && (
              <>
                <div className="d-flex align-items-center gap-2 mb-2 mt-3">
                  <h6 className="mb-0">SKU template selectors</h6>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setShowSku((v) => !v)}
                  >
                    {showSku ? "Hide" : "Show"}
                  </Button>
                </div>
                {showSku && (
                  <pre
                    className="bg-light p-2 rounded small"
                    style={{ maxHeight: 250, overflowY: "auto", fontSize: 11 }}
                  >
                    {JSON.stringify(evaluation.skuTemplate, null, 2)}
                  </pre>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────

type PartFilterValue = "" | "pass" | "fail" | "not_tested";

interface IEvaluationFilters {
  domain: string;
  country: string;
  aiCalled: "" | "true" | "false";
  origin: "" | "extension" | "session";
  autoTemplateStatus: PartFilterValue;
  skuTemplateStatus: PartFilterValue;
}

const EMPTY_FILTERS: IEvaluationFilters = {
  domain: "",
  country: "",
  aiCalled: "",
  origin: "",
  autoTemplateStatus: "",
  skuTemplateStatus: "",
};

export const AutoCouponEvaluations: React.FC = () => {
  const [page, setPage] = useState(1);
  // Pending = what the inputs hold; applied = what the query uses. The
  // Search button copies pending → applied.
  const [pendingFilters, setPendingFilters] =
    useState<IEvaluationFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] =
    useState<IEvaluationFilters>(EMPTY_FILTERS);
  const [selectedEval, setSelectedEval] =
    useState<IAutoCouponEvaluation | null>(null);

  // Deep links (e.g. from the Session Runs page) can pin a correlationId.
  const [searchParams] = useSearchParams();
  const correlationId = searchParams.get("correlationId") ?? "";

  const queryParams = new URLSearchParams({ page: String(page), limit: "25" });
  if (correlationId) queryParams.set("correlationId", correlationId);
  if (appliedFilters.domain) queryParams.set("domain", appliedFilters.domain);
  if (appliedFilters.country)
    queryParams.set("country", appliedFilters.country);
  if (appliedFilters.aiCalled)
    queryParams.set("aiCalled", appliedFilters.aiCalled);
  if (appliedFilters.origin) queryParams.set("origin", appliedFilters.origin);
  if (appliedFilters.autoTemplateStatus)
    queryParams.set("autoTemplateStatus", appliedFilters.autoTemplateStatus);
  if (appliedFilters.skuTemplateStatus)
    queryParams.set("skuTemplateStatus", appliedFilters.skuTemplateStatus);

  const { data, loading, error, refetch } =
    useGet<IAutoCouponEvaluationListResponse>(
      `/auto-coupons/evaluations?${queryParams}`
    );

  const applyFilters = () => {
    setAppliedFilters(pendingFilters);
    setPage(1);
  };

  const clearFilters = () => {
    setPendingFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const onFilterKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") applyFilters();
  };

  const evaluations = data?.evaluations ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 25);

  const successRate = (e: IAutoCouponEvaluation): number => {
    if (!e.codes.length) return 0;
    return Math.round(
      (e.codes.filter((c) => c.success).length / e.codes.length) * 100
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Auto Coupon Evaluations</h4>
        <Button variant="outline-primary" size="sm" onClick={() => refetch()}>
          <RefreshCw size={14} className="me-1" /> Refresh
        </Button>
      </div>

      <Card className="mb-3">
        <Card.Body>
          <Row className="align-items-end g-2">
            <Col md={3}>
              <Form.Label className="small">Domain</Form.Label>
              <Form.Control
                size="sm"
                placeholder="e.g. chelseacharles.com"
                value={pendingFilters.domain}
                onKeyDown={onFilterKeyDown}
                onChange={(e) =>
                  setPendingFilters((f) => ({ ...f, domain: e.target.value }))
                }
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small">Country</Form.Label>
              <Form.Control
                size="sm"
                placeholder="e.g. US"
                value={pendingFilters.country}
                onKeyDown={onFilterKeyDown}
                onChange={(e) =>
                  setPendingFilters((f) => ({ ...f, country: e.target.value }))
                }
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small">AI called</Form.Label>
              <Form.Select
                size="sm"
                value={pendingFilters.aiCalled}
                onChange={(e) =>
                  setPendingFilters((f) => ({
                    ...f,
                    aiCalled: e.target.value as IEvaluationFilters["aiCalled"],
                  }))
                }
              >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small">Run by</Form.Label>
              <Form.Select
                size="sm"
                value={pendingFilters.origin}
                onChange={(e) =>
                  setPendingFilters((f) => ({
                    ...f,
                    origin: e.target.value as IEvaluationFilters["origin"],
                  }))
                }
              >
                <option value="">All</option>
                <option value="extension">User (extension)</option>
                <option value="session">Agentic (session)</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small">Auto template</Form.Label>
              <Form.Select
                size="sm"
                value={pendingFilters.autoTemplateStatus}
                onChange={(e) =>
                  setPendingFilters((f) => ({
                    ...f,
                    autoTemplateStatus: e.target
                      .value as IEvaluationFilters["autoTemplateStatus"],
                  }))
                }
              >
                <option value="">All</option>
                <option value="pass">Pass</option>
                <option value="fail">Fail</option>
                <option value="not_tested">Not tested</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small">SKU template</Form.Label>
              <Form.Select
                size="sm"
                value={pendingFilters.skuTemplateStatus}
                onChange={(e) =>
                  setPendingFilters((f) => ({
                    ...f,
                    skuTemplateStatus: e.target
                      .value as IEvaluationFilters["skuTemplateStatus"],
                  }))
                }
              >
                <option value="">All</option>
                <option value="pass">Pass</option>
                <option value="fail">Fail</option>
                <option value="not_tested">Not tested</option>
              </Form.Select>
            </Col>
            <Col md={3} className="d-flex gap-2">
              <Button size="sm" variant="primary" onClick={applyFilters}>
                Search
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={clearFilters}
              >
                Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {error && <Alert variant="danger">Failed to load evaluations.</Alert>}

      {!loading && !error && (
        <>
          <Table hover responsive size="sm">
            <thead>
              <tr>
                <th>Correlation ID</th>
                <th>Domain</th>
                <th>Country</th>
                <th>Run by</th>
                <th>User</th>
                <th>Ext. version</th>
                <th>Mode</th>
                <th>Templates</th>
                <th>Codes</th>
                <th>Success rate</th>
                <th>Best saving</th>
                <th>AI called</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {evaluations.length === 0 && (
                <tr>
                  <td colSpan={13} className="text-center text-muted py-4">
                    No evaluations found.
                  </td>
                </tr>
              )}
              {evaluations.map((ev) => {
                const rate = successRate(ev);
                const bestSaving = ev.codes.reduce(
                  (max, c) => (c.success && c.savings > max ? c.savings : max),
                  0
                );
                return (
                  <tr
                    key={ev._id}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedEval(ev)}
                  >
                    <td className="font-monospace small text-muted">
                      {ev.correlationId.slice(0, 8)}…
                    </td>
                    <td>{ev.domain}</td>
                    <td>{ev.country}</td>
                    <td>
                      {ev.origin === "session" ? (
                        <Badge bg="dark">Agentic</Badge>
                      ) : (
                        <Badge bg="light" text="dark">
                          User
                        </Badge>
                      )}
                    </td>
                    <td className="small text-muted">
                      {ev.userEmail ?? "—"}
                    </td>
                    <td className="small font-monospace text-muted">
                      {ev.extensionVersion ? `v${ev.extensionVersion}` : "—"}
                    </td>
                    <td>
                      <Badge
                        bg={ev.templateMode === "new" ? "warning" : "info"}
                        text="dark"
                      >
                        {ev.templateMode}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-1 align-items-center small">
                        <span className="text-muted">A</span>
                        <PartBadge status={ev.autoTemplateStatus} />
                        <span className="text-muted ms-1">S</span>
                        <PartBadge status={ev.skuTemplateStatus} />
                      </div>
                    </td>
                    <td>{ev.codes.length}</td>
                    <td>
                      <span
                        className={
                          rate === 0
                            ? "text-danger"
                            : rate === 100
                              ? "text-success"
                              : "text-warning"
                        }
                      >
                        {rate === 100 ? (
                          <CheckCircle size={12} className="me-1" />
                        ) : rate === 0 ? (
                          <XCircle size={12} className="me-1" />
                        ) : null}
                        {rate}%
                      </span>
                    </td>
                    <td>
                      {bestSaving > 0
                        ? `${ev.currency ?? ""}${bestSaving.toFixed(2)}`
                        : "—"}
                    </td>
                    <td>
                      {ev.aiCalled ? (
                        <Badge bg="primary">Yes</Badge>
                      ) : (
                        <span className="text-muted small">No</span>
                      )}
                    </td>
                    <td className="small text-muted">
                      {new Date(ev.createdAt).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <small className="text-muted">{total} total</small>
              <div className="d-flex gap-1">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Prev
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <EvaluationDetailModal
        evaluation={selectedEval}
        onClose={() => setSelectedEval(null)}
      />
    </div>
  );
};
